import type { TPaycallWebhookPayload } from "./paycall.types";

/** Dev SIP-bridge mock values only — prod payloads come from Paycall external webhooks. */
export const PAYCALL_DEV_MOCK = {
  custId: "7854",
  custName: "מטופל דמו",
  did: "0738507388",
  groupName: "Bpreven Demo",
  recordingUrl:
    "https://records.webpbx.co.il//RECORDINGS/20260708173007_0504165772_0738507388-all.mp3",
} as const;

/** Queue / DID extension used as `callee` on inbound SIP-bridge webhooks. */
export const PAYCALL_INBOUND_CALLEE = "1000";

export function nowPaycallTimestamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
    `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  );
}

export function buildStartWebhook(params: {
  caller: string;
  callee: string;
  uniqueid: string;
  startTime: string;
  useDevMocks: boolean;
  callDirection?: "Inbound" | "Outbound";
}): TPaycallWebhookPayload {
  const direction = params.callDirection ?? "Inbound";
  const isOutbound = direction === "Outbound";
  
  return {
    status: "START",
    callerid: isOutbound ? params.caller : undefined,
    calleridnumber: isOutbound ? params.caller : undefined,
    caller: params.caller,
    callee: params.callee,
    uniqueid: params.uniqueid,
    exten: isOutbound ? params.caller : undefined,
    custid: params.useDevMocks ? PAYCALL_DEV_MOCK.custId : undefined,
    did: params.useDevMocks ? PAYCALL_DEV_MOCK.did : undefined,
    call_direction: direction,
    start_time: params.startTime,
    click2_group: isOutbound ? "Y" : undefined,
  };
}

export function buildAnswerWebhook(params: {
  caller: string;
  callee: string;
  uniqueid: string;
  exten: string;
  answerTime: string;
  callDirection?: "Inbound" | "Outbound";
}): TPaycallWebhookPayload {
  const direction = params.callDirection ?? "Inbound";
  
  return {
    status: "ANSWER",
    callerid: params.caller || "",
    calleridnumber: params.caller || "",
    caller: params.caller,
    callee: params.callee,
    uniqueid: params.uniqueid,
    exten: params.exten,
    answer_time: params.answerTime,
    call_direction: direction,
  };
}

export function buildEndWebhook(params: {
  caller: string;
  callee: string;
  uniqueid: string;
  exten: string;
  disposition: string;
  hangupReason: string;
  startTime: string;
  answerTime: string;
  endTime: string;
  totalSec: number;
  answerSec: number;
  did: string;
  useDevMocks: boolean;
  callDirection?: "Inbound" | "Outbound";
}): TPaycallWebhookPayload {
  const answered = params.disposition === "Answer";
  const recording = answered && params.useDevMocks ? PAYCALL_DEV_MOCK.recordingUrl : "";
  const direction = params.callDirection ?? "Inbound";
  const isOutbound = direction === "Outbound";

  // For outbound calls, fix field mappings
  const callerIdValue = isOutbound ? "0738507388" : (params.caller || "");
  const managementLink = isOutbound 
    ? `https://web.multivoice.co.il/template//${params.uniqueid}`
    : recording;

  return {
    status: "END",
    callerid: callerIdValue,
    calleridnumber: callerIdValue,
    caller: params.caller,
    callee: params.callee,
    custid: params.useDevMocks ? PAYCALL_DEV_MOCK.custId : undefined,
    custname: params.useDevMocks ? PAYCALL_DEV_MOCK.custName : undefined,
    uniqueid: params.uniqueid,
    exten: params.exten,
    hangup_reason: params.hangupReason,
    total_duration: String(params.totalSec),
    answer_duration: String(params.answerSec),
    dlink: recording,
    groupname: params.useDevMocks ? PAYCALL_DEV_MOCK.groupName : undefined,
    start_time: params.startTime,
    answer_time: answered ? params.answerTime : "",
    end_time: params.endTime,
    disposition: params.disposition,
    did: params.did || (params.useDevMocks ? PAYCALL_DEV_MOCK.did : undefined),
    call_direction: direction,
    multivoice_link: managementLink,
  };
}
